/**
 * alatunniste-roskaposti.js — the contact form's spam scorer.
 *
 * Ported 2026-09-23 from heidisimelius.fi (repo at
 * ~/Documents/Freelancin/projects/valmiit/HeidiSimelius/heidisimelius,
 * api/_lib/spamCheck.ts, commit ef69774) to plain browser JavaScript: same
 * weights, thresholds, regexes and helper logic. What differs, and why:
 *   - it runs IN THE BROWSER, not on a server. Shopify's contact form posts
 *     straight to Shopify, there is no code of ours in between, and the
 *     recipient cannot be changed, so Heidi's "reroute spam to the tech inbox"
 *     has no equivalent here. assets/alatunniste-yhteydenotto.js decides what
 *     each verdict does; this file only scores;
 *   - the reasons are in Finnish, because the one person who reads them is
 *     Susanna, in the notification email of a "suspect" message;
 *   - Heidi's `location` / `eventType` fields do not exist on this form, so
 *     only the phone is scored as an "other field";
 *   - three Finnish SEO-pitch phrases join the English list (see SPAM_PHRASES).
 *
 * Loaded as a classic deferred script BEFORE alatunniste-yhteydenotto.js and
 * exposed as `globalThis.susannaRoskaposti`. scripts/test-roskaposti.mjs runs
 * it in Node as the same classic script (via `vm`) and reads that global; the
 * `module.exports` line at the end serves any CommonJS loader, but note that
 * Horizon's assets/package.json says "type": "module", so a plain `require()`
 * of this file from Node gets an ES module instead. Everything sits inside an
 * IIFE: classic scripts share one global scope, and a top-level `const` here
 * would collide with any other script that happened to use the same name.
 *
 * ----------------------------------------------------------------------------
 * From the original, and still the whole point:
 *
 * Additive scoring: each independent signal adds a weight, and the total maps
 * to a verdict. Every hit is explained in `reasons`.
 *
 * The top priority is NO FALSE POSITIVES — a real customer must never be
 * "spam". So no single signal (except the honeypot) can reach the spam
 * threshold on its own; "spam" always needs at least two independent signals.
 * Non-Latin scripts (Cyrillic, CJK, …) are deliberately never a signal:
 * international customers exist. Human quirks that look random are excused
 * rather than scored: caps-lock typing ("mATTIvIRTANEN"), all-caps words and
 * acronyms ("EsaPekkaVIRTANEN", "YLE:n"), one-row keyboard mashes
 * ("asdfghjkl"), and pasted codes with digits score lower.
 *
 * The weekly bot pattern this targets:
 *   name "DZiZYBAuexMthkZAU", email "m.o.r.e.co.p.exu.t32@gmail.com",
 *   message "sMFXjOECFaCAQTQxBTLYD"
 *
 * Input:  { name, email, message, phone?, website? (honeypot), elapsedMs? }
 * Output: { verdict: 'ham' | 'suspect' | 'spam', score, botShape, reasons: string[] }
 */
(() => {
  /** Score at or above which a submission is "suspect" / "spam". */
  const SPAM_THRESHOLDS = Object.freeze({ suspect: 3, spam: 6 });

  /**
   * Signal weights. Strong = 4 (suspect alone), medium = 2–3, weak = 1.
   * The largest non-honeypot weight is below `SPAM_THRESHOLDS.spam`.
   */
  const SPAM_WEIGHTS = Object.freeze({
    honeypot: 100,
    fastSubmit: 4,
    nameGibberish: 4,
    messageGibberish: 4,
    /*
     * Message is a random token WITH digits – also what a pasted order code or
     * video ID ("4cOdK2wGLETKBW3PvgPWqT") looks like, so one notch weaker than
     * letters-only gibberish and never combined with `singleTokenFields`.
     */
    messageCode: 3,
    /*
     * Name / message made of several short random-case words ("fGhJk LqWeRt")
     * – the bot string with spaces inserted. Weaker than a gibberish token:
     * suspect alone, spam only when both fields (or another signal) agree.
     */
    spacedGibberish: 3,
    /*
     * Name AND message are each one long mixed-case token – the bot's shape
     * even when the gibberish test misses one or both of them. Suspect alone;
     * spam with one gibberish hit.
     */
    singleTokenFields: 3,
    /* Per field (here only the phone), capped by `otherFieldsCap`. */
    otherFieldGibberish: 2,
    otherFieldsCap: 4,
    /* Phone field with letters but no digits at all. */
    phoneWithoutDigits: 1,
    /* gmail local part with ≥4 dots or ≥4 single-character segments. */
    gmailDotsHeavy: 2,
    /* gmail local part with 3 dots (e.g. "j.p.k.laine" – real people do this). */
    gmailDotsLight: 1,
    linkMarkup: 3,
    urlInName: 3,
    manyUrls: 2,
    /* Per distinct marketing phrase, capped by `spamPhraseCap`. */
    spamPhrase: 1,
    spamPhraseCap: 2,
  });

  /** Submissions faster than this (from the form becoming interactive) are treated as automated. */
  const FAST_SUBMIT_MS = 3000;
  const MIN_GIBBERISH_TOKEN_LENGTH = 8;
  /** Share of letters that must be irregular capitals for multi-word random-case text. */
  const RANDOM_TEXT_MIN_IRREGULAR_RATIO = 0.15;
  const MANY_URLS = 3;

  // -------------------------------------------------------------------------
  // Gibberish detection
  // -------------------------------------------------------------------------

  const TOKEN_RE = /^[\p{L}\p{M}\p{N}]+$/u;
  const LETTER_RE = /\p{L}/u;
  const DIGIT_RE = /\p{N}/u;
  const UPPER_RE = /\p{Lu}/u;
  const LOWER_RE = /\p{Ll}/u;
  /*
   * Base Latin letters (after stripping diacritics) the vowel heuristic
   * understands. Finnish is covered: NFD turns ä/ö/å into a/o/a plus a
   * combining mark, the mark is stripped, and a/o and y are all in the vowel
   * set — so "Äijälä" and "Östman" are as pronounceable here as they are aloud.
   */
  const LATIN_BASE_RE = /^[a-zßæøœłđþ]$/;
  const LATIN_VOWELS = new Set(['a', 'e', 'i', 'o', 'u', 'y', 'æ', 'ø', 'œ']);

  const isUpper = (ch) => ch !== undefined && UPPER_RE.test(ch);
  const isLower = (ch) => ch !== undefined && LOWER_RE.test(ch);

  /**
   * Vowel ratio and longest consonant run of a Latin-script word, or null when
   * the word contains non-Latin letters (the heuristic means nothing for other
   * scripts).
   */
  function latinShape(letters) {
    const base = letters.map((ch) => ch.normalize('NFD').replaceAll(/\p{M}/gu, '').toLowerCase());
    if (base.length === 0 || !base.every((ch) => LATIN_BASE_RE.test(ch))) {
      return null;
    }

    let vowels = 0;
    let run = 0;
    let maxConsonantRun = 0;
    for (const ch of base) {
      if (LATIN_VOWELS.has(ch)) {
        vowels++;
        run = 0;
      } else {
        run++;
        maxConsonantRun = Math.max(maxConsonantRun, run);
      }
    }
    return { vowelRatio: vowels / base.length, maxConsonantRun };
  }

  /*
   * Case stats: letters, upper, lowerToUpper (a lowercase letter followed by a
   * capital), irregularUpper (an inner capital NOT followed by two lowercase
   * letters).
   */
  const NO_FLIPS = Object.freeze({ letters: 0, upper: 0, lowerToUpper: 0, irregularUpper: 0 });

  /**
   * All-caps runs at the edges of a word are words, not noise: a leading
   * acronym of 2+ letters before a capitalised word is dropped ("JPK|Laine",
   * "KP|DeLaCruz", "NV|Škrjanc") and a trailing all-caps word of 3+ letters
   * shrinks to its first capital ("EsaPekka|VIRTANEN", "Heidi|FANS"). Words
   * without lowercase are unchanged.
   */
  function trimCapsEdges(word) {
    let start = 0;
    while (isUpper(word[start]) && isUpper(word[start + 1])) start++;
    const letters = start >= 2 && isLower(word[start + 1]) ? word.slice(start) : word;

    let end = letters.length;
    while (end > 0 && isUpper(letters[end - 1])) end--;
    return end > 0 && letters.length - end >= 3 ? letters.slice(0, end + 1) : letters;
  }

  /**
   * Case transitions of one word (letters only), after `trimCapsEdges`.
   *
   * An inner capital is "regular" when two lowercase letters follow it — the
   * shape of every camel-case word start ("McDonald", "DeAndre", "VanDerBerg",
   * "KeikkaPalvelu", "YouTube"). Random strings have many irregular ones
   * (followed by another capital, a single lowercase letter, or the end).
   * All-caps words have no flips at all.
   */
  function caseStats(word) {
    if (!word.some(isLower)) return { ...NO_FLIPS, letters: word.length, upper: word.length };

    const letters = trimCapsEdges(word);
    const stats = {
      ...NO_FLIPS,
      letters: letters.length,
      upper: isUpper(letters[0]) ? 1 : 0,
    };
    for (let i = 1; i < letters.length; i++) {
      if (!isUpper(letters[i])) continue;
      stats.upper++;
      if (isLower(letters[i - 1])) stats.lowerToUpper++;
      if (!(isLower(letters[i + 1]) && isLower(letters[i + 2]))) stats.irregularUpper++;
    }
    return stats;
  }

  /** Case keeps flipping back and forth with irregular capitals. */
  const flipsCaseOften = (s) => s.lowerToUpper >= 2 && s.irregularUpper >= 3;

  /** Swap upper and lower case. */
  const invertCase = (letters) => letters.map((ch) => (isUpper(ch) ? ch.toLowerCase() : ch.toUpperCase()));

  const hasLatinVowel = (letters) => {
    const shape = latinShape(letters);
    return shape === null || shape.vowelRatio > 0;
  };

  /**
   * Words typed with caps lock on ("mATTIvIRTANEN", "pRIYAmCdONALD", "hEI"):
   * they start lowercase, never have two lowercase letters in a row, and
   * swapping the case gives ordinary camel case ("PriyaMcDonald") made of real
   * words – parts of 3+ letters that all have vowels. That keeps the bot's
   * "sMFXjOECFaCAQTQxBTLYD" (→ "Smfx|Joecf|…") and short random words
   * ("zXcV" → "Zx|Cv") random.
   */
  function isCapsLockTyped(word) {
    if (!isLower(word[0]) || word.some((ch, i) => isLower(ch) && isLower(word[i + 1]))) {
      return false;
    }
    const swapped = invertCase(word);
    if (flipsCaseOften(caseStats(swapped))) return false;

    const parts = [];
    for (const ch of swapped) {
      if (isUpper(ch) || parts.length === 0) parts.push([]);
      parts.at(-1).push(ch);
    }
    const words = parts.filter((part) => part.length >= 3);
    return words.length > 0 && words.every(hasLatinVowel);
  }

  /** Random mixed case in one word, e.g. "DZiZYBAuexMthkZAU" / "sMFXjOECFaCAQTQxBTLYD". */
  function hasRandomCasing(letters) {
    // All-caps ("PIKKUJOULUT") and caseless scripts are never random casing.
    if (!letters.some(isLower) || isCapsLockTyped(letters)) return false;

    const stats = caseStats(letters);
    if (flipsCaseOften(stats)) return true;

    // Mixed case that also isn't pronounceable ("RcKwTnpLmxQv", "KdiJgjahRMst").
    const shape = latinShape(trimCapsEdges(letters));
    return (
      stats.lowerToUpper >= 2 &&
      stats.upper / stats.letters >= 0.25 &&
      shape !== null &&
      shape.vowelRatio < 0.3 &&
      shape.maxConsonantRun >= 4
    );
  }

  /** URLs, e-mail addresses, @handles and domains carry random IDs – not words. */
  const LINK_LIKE_TOKEN_RE = /[/@]|\p{L}\.\p{L}{2,}/u;
  const LETTER_RUN_RE = /\p{L}+/gu;

  /**
   * Several short words whose case flips at random, i.e. the bot string with
   * spaces inserted ("fGhJk LqWeRt", "zXcV bNmQ wErT yUiO"). Flips are summed
   * over the field; caps-lock words, all-caps words, acronyms ("YLE:n") and
   * 1–2 letter words ("xD") add nothing, and the flips must be dense so the odd
   * "MaRiA" in a sentence never adds up.
   */
  function isRandomCaseText(value) {
    const words = value
      .normalize('NFC')
      .split(/\s+/)
      .filter((token) => !LINK_LIKE_TOKEN_RE.test(token))
      .flatMap((token) => token.match(LETTER_RUN_RE) ?? [])
      .map((word) => Array.from(word));
    if (words.length < 2) return false;

    const total = words
      .map((word) => (word.length < 3 || isCapsLockTyped(word) ? { ...NO_FLIPS, letters: word.length } : caseStats(word)))
      .reduce((sum, s) => ({
        letters: sum.letters + s.letters,
        upper: sum.upper + s.upper,
        lowerToUpper: sum.lowerToUpper + s.lowerToUpper,
        irregularUpper: sum.irregularUpper + s.irregularUpper,
      }));
    return (
      total.letters >= MIN_GIBBERISH_TOKEN_LENGTH &&
      flipsCaseOften(total) &&
      total.irregularUpper / total.letters >= RANDOM_TEXT_MIN_IRREGULAR_RATIO
    );
  }

  const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

  /** Humans mash one keyboard row ("asdfghjkl", "sdfsdfsdf"); random bot strings don't. */
  const isSingleKeyboardRow = (letters) =>
    KEYBOARD_ROWS.some((row) => letters.every((ch) => row.includes(ch.toLowerCase())));

  /**
   * Keyboard-mash consonant clusters in a Latin word ("xkcdqwrtzpvbnm"). Kept
   * strict so real names with heavy clusters ("Mkrtchyan",
   * "Brzęczyszczykiewicz", "Szczepański") stay clear.
   */
  function isUnpronounceable(word) {
    const letters = trimCapsEdges(word);
    if (letters.length < MIN_GIBBERISH_TOKEN_LENGTH || isSingleKeyboardRow(letters)) {
      return false;
    }
    const shape = latinShape(letters);
    return shape !== null && shape.vowelRatio < 0.12 && shape.maxConsonantRun >= 6;
  }

  /**
   * True when `token` is a single whitespace-free run of letters/digits
   * (length ≥ 8) that looks machine-generated: random mixed case or no
   * pronounceable structure.
   */
  function isGibberishToken(token) {
    const normalized = token.normalize('NFC');
    if (!TOKEN_RE.test(normalized)) return false;

    const chars = Array.from(normalized);
    if (chars.length < MIN_GIBBERISH_TOKEN_LENGTH) return false;

    const letters = chars.filter((ch) => LETTER_RE.test(ch));
    return hasRandomCasing(letters) || isUnpronounceable(letters);
  }

  /** One whitespace-free letters/digits token (length ≥ 8) with a capital after its first letter. */
  function isMixedCaseToken(value) {
    if (!TOKEN_RE.test(value) || Array.from(value).length < MIN_GIBBERISH_TOKEN_LENGTH) {
      return false;
    }
    const letters = Array.from(value).filter((ch) => LETTER_RE.test(ch));
    return letters.some(isLower) && letters.slice(1).some(isUpper);
  }

  /** Whole field is made of gibberish tokens only (usually a single token). */
  function isGibberishField(value) {
    const tokens = value.trim().split(/\s+/).filter(Boolean);
    return tokens.length > 0 && tokens.every(isGibberishToken);
  }

  // -------------------------------------------------------------------------
  // Other signals
  // -------------------------------------------------------------------------

  const GMAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com']);

  /**
   * Gmail ignores dots, so bots spray dot variants of one mailbox
   * ("m.o.r.e.co.p.exu.t32"). Real people use a few ("j.p.k.laine"), and one
   * dot ("matti.meikalainen") is simply normal.
   */
  function gmailDotSignal(email) {
    const at = email.lastIndexOf('@');
    if (at <= 0) return null;
    const local = email.slice(0, at);
    const domain = email.slice(at + 1).trim().toLowerCase();
    if (!GMAIL_DOMAINS.has(domain)) return null;

    const segments = local.split('.');
    const dots = segments.length - 1;
    const singleCharSegments = segments.filter((s) => s.length === 1).length;

    if (dots >= 4 || singleCharSegments >= 4) {
      return {
        weight: SPAM_WEIGHTS.gmailDotsHeavy,
        reason: `gmail-osoitteessa ${dots} pistettä / ${singleCharSegments} yhden merkin osaa`,
      };
    }
    if (dots === 3) {
      return { weight: SPAM_WEIGHTS.gmailDotsLight, reason: 'gmail-osoitteessa 3 pistettä' };
    }
    return null;
  }

  const URL_RE = /\bhttps?:\/\/\S+|\bwww\.\S+/gi;
  const NAME_URL_RE = /https?:\/\/|\bwww\.|\[url|<a\s/i;
  const LINK_MARKUP_RE = /\[url[=\]]|\[link[=\]]|<a\s+[^>]*href/i;

  /*
   * Marketing / SEO / crypto pitches. Weak on their own by design (+1 each,
   * capped at +2, so phrases alone can never even reach "suspect").
   *
   * The English list is Heidi's, unchanged. The three Finnish ones added here
   * are SEO-seller vocabulary that a person writing to a life coach has no
   * reason to use. ⚠️ Deliberately NOT added, because they are ordinary topics
   * for a coaching client: money, debt, stress, relationships, gambling
   * ("kasino" — someone may be writing about exactly that), work, "yrittäjä",
   * "markkinointi", "näkyvyys", "hakukoneoptimointi" (someone's job title),
   * "Googlen etusivu" ("löysin sinut Googlen etusivulta" is a real sentence).
   */
  const SPAM_PHRASES = [
    { label: 'SEO', re: /\bSEO\b/ },
    { label: 'backlinks', re: /\bback-?links?\b/i },
    { label: 'rank your website', re: /\brank(?:ing)? (?:your|ur) (?:web)?site\b/i },
    { label: 'first page of google', re: /\b(?:first|1st|top) page (?:of|on) google\b/i },
    { label: 'website traffic', re: /\b(?:website|web|site|organic) traffic\b/i },
    { label: 'guest post', re: /\bguest[- ]?post(?:s|ing)?\b/i },
    { label: 'digital marketing', re: /\bdigital marketing\b/i },
    { label: 'lead generation', re: /\blead generation\b/i },
    { label: 'crypto', re: /\bcrypto(?:currenc(?:y|ies))?\b/i },
    { label: 'bitcoin', re: /\bbitcoins?\b/i },
    { label: 'forex', re: /\bforex\b/i },
    { label: 'casino', re: /\bcasinos?\b/i },
    { label: 'viagra/cialis', re: /\b(?:viagra|cialis)\b/i },
    { label: 'earn money', re: /\b(?:earn|make) (?:\$?\d+\S* )?money\b/i },
    { label: 'investment opportunity', re: /\binvestment opportunit(?:y|ies)\b/i },
    { label: 'click here', re: /\bclick here\b/i },
    { label: 'unsubscribe', re: /\bunsubscribe\b/i },
    // Finnish, added 2026-09-23. `\b` is ASCII-only in JS, so these anchor on
    // the ASCII start of the word and let the Finnish inflection run free.
    { label: 'takaisinlinkit', re: /\btakaisinlink/i },
    { label: 'linkkipaketti', re: /\blinkkipaket/i },
    { label: 'vierasartikkeli', re: /\bvieras(?:artikkel|kirjoitu|blogi)/i },
  ];

  function spamPhraseSignal(message) {
    const hits = SPAM_PHRASES.filter(({ re }) => re.test(message)).map(({ label }) => label);
    if (hits.length === 0) return null;
    return {
      weight: Math.min(hits.length * SPAM_WEIGHTS.spamPhrase, SPAM_WEIGHTS.spamPhraseCap),
      reason: `markkinointifraaseja: ${hits.join(', ')}`,
    };
  }

  /* Heidi scores phone, location and event type here; this form has a phone only. */
  function otherFieldsSignal(input) {
    const fields = [['puhelin', input.phone]];
    const gibberish = fields
      .filter(([, value]) => typeof value === 'string' && (isGibberishField(value) || isRandomCaseText(value)))
      .map(([label]) => label);

    if (gibberish.length > 0) {
      return {
        weight: Math.min(gibberish.length * SPAM_WEIGHTS.otherFieldGibberish, SPAM_WEIGHTS.otherFieldsCap),
        reason: `satunnaista merkkijonoa kentässä: ${gibberish.join(', ')}`,
      };
    }

    const phone = typeof input.phone === 'string' ? input.phone.trim() : '';
    if (phone.length > 0 && !/\d/.test(phone) && /\p{L}/u.test(phone)) {
      return { weight: SPAM_WEIGHTS.phoneWithoutDigits, reason: 'puhelinnumerossa ei ole numeroita' };
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Assessment
  // -------------------------------------------------------------------------

  const asString = (value) => (typeof value === 'string' ? value : '');

  function collectSignals(input) {
    const name = asString(input.name);
    const message = asString(input.message);
    const signals = [];
    const add = (signal) => {
      if (signal) signals.push(signal);
    };

    if (asString(input.website).trim().length > 0) {
      add({ weight: SPAM_WEIGHTS.honeypot, reason: 'piilokenttä täytetty' });
    }

    const elapsed = input.elapsedMs;
    if (typeof elapsed === 'number' && Number.isFinite(elapsed) && elapsed >= 0 && elapsed < FAST_SUBMIT_MS) {
      add({
        weight: SPAM_WEIGHTS.fastSubmit,
        reason: `lähetetty ${Math.round(elapsed)} ms sivun latauksesta (alle ${FAST_SUBMIT_MS} ms)`,
      });
    }

    if (isGibberishField(name)) {
      add({ weight: SPAM_WEIGHTS.nameGibberish, reason: 'nimi on satunnainen merkkijono', field: 'name' });
    } else if (isRandomCaseText(name)) {
      add({ weight: SPAM_WEIGHTS.spacedGibberish, reason: 'nimi on satunnaisin isoin kirjaimin', field: 'name' });
    }
    const messageHasDigits = DIGIT_RE.test(message);
    if (isGibberishField(message)) {
      add(
        messageHasDigits
          ? { weight: SPAM_WEIGHTS.messageCode, reason: 'viesti on satunnainen koodi', field: 'message' }
          : { weight: SPAM_WEIGHTS.messageGibberish, reason: 'viesti on satunnainen merkkijono', field: 'message' },
      );
    } else if (isRandomCaseText(message)) {
      add({ weight: SPAM_WEIGHTS.spacedGibberish, reason: 'viesti on satunnaisin isoin kirjaimin', field: 'message' });
    }
    if (!messageHasDigits && isMixedCaseToken(name.trim()) && isMixedCaseToken(message.trim())) {
      add({
        weight: SPAM_WEIGHTS.singleTokenFields,
        reason: 'nimi ja viesti ovat kumpikin yksi sekakirjaiminen sana',
      });
    }

    add(otherFieldsSignal(input));
    add(gmailDotSignal(asString(input.email)));

    if (NAME_URL_RE.test(name)) {
      add({ weight: SPAM_WEIGHTS.urlInName, reason: 'nimessä on linkki' });
    }
    if (LINK_MARKUP_RE.test(message)) {
      add({ weight: SPAM_WEIGHTS.linkMarkup, reason: 'viestissä on BBCode- tai HTML-linkki' });
    }
    const urlCount = message.match(URL_RE)?.length ?? 0;
    if (urlCount >= MANY_URLS) {
      add({ weight: SPAM_WEIGHTS.manyUrls, reason: `viestissä ${urlCount} linkkiä` });
    }

    add(spamPhraseSignal(message));

    return signals;
  }

  function assessSpam(input) {
    const signals = collectSignals(input ?? {});
    const score = signals.reduce((sum, s) => sum + s.weight, 0);
    let verdict = 'ham';
    if (score >= SPAM_THRESHOLDS.spam) verdict = 'spam';
    else if (score >= SPAM_THRESHOLDS.suspect) verdict = 'suspect';

    /* ADDED HERE, NOT IN HEIDI'S CODE (2026-09-23): `botShape`. Heidi's
       verdict decided a ROUTE — spam went to a tech inbox, so nothing was ever
       lost. Here there is no second inbox, and the form's only options are
       "send" or "block". A 6-point score is reachable by real people (a pasted
       form after Shopify's error page trips the timer; a marketer's signature
       with three links and a dotted gmail), so the form blocks ONLY this:
       the NAME and the MESSAGE both machine-generated — the exact shape of the
       weekly bot in Heidi's commit ef69774. Every other score is sent, tagged.
       Scores and verdicts are untouched, so the port stays diffable. */
    const flagged = new Set(signals.map((s) => s.field).filter(Boolean));
    return {
      verdict,
      score,
      botShape: flagged.has('name') && flagged.has('message'),
      reasons: signals.map((s) => `${s.reason} (+${s.weight})`),
    };
  }

  const api = Object.freeze({
    assessSpam,
    isGibberishToken,
    isRandomCaseText,
    SPAM_WEIGHTS,
    SPAM_THRESHOLDS,
    FAST_SUBMIT_MS,
  });

  globalThis.susannaRoskaposti = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
